import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useStore } from '../../app/StoreContext';
import { useApp } from '../../app/AppContext';
import { callFn } from '../../lib/api';
import { OrderCard } from '../../components/OrderCard';
import { SaveDetailsCard } from '../../components/SaveDetailsCard';
import type { Order } from '../../lib/types';
import type { Contact } from '../../lib/contact';

interface OrdersNavState { highlight?: string; offerAccount?: boolean; contact?: Contact; }

export function Orders() {
  const { slug, boundHere } = useStore();
  const { authed, isGuest, ready, toast } = useApp();
  const [orders, setOrders] = useState<Order[] | null>(null);
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navState = (location.state as OrdersNavState | null) ?? null;
  const highlight = navState?.highlight;
  const [offer, setOffer] = useState<boolean>(!!navState?.offerAccount);
  const signedIn = authed;

  const load = useCallback(async () => {
    if (!signedIn) { setOrders(null); return; }
    try { const r = await callFn<{ orders: Order[] }>('list_my_orders'); setOrders(r.orders); }
    catch (e) { toast(e instanceof Error ? e.message : 'Could not load orders.', 'err'); }
  }, [signedIn, toast]);

  // Return leg of a redirect-based payment method: finish the order.
  useEffect(() => {
    const confirm = params.get('confirm');
    if (!confirm || !signedIn) return;
    callFn('confirm_payment', { orderId: confirm })
      .then(() => toast('Payment received — order placed.', 'ok'))
      .catch((e) => toast(e instanceof Error ? e.message : 'Payment could not be confirmed.', 'err'))
      .finally(() => { params.delete('confirm'); setParams(params, { replace: true }); void load(); });
  }, [params, setParams, signedIn, toast, load]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (!signedIn) return;
    const active = orders?.some((o) => !['picked_up', 'delivered', 'cancelled'].includes(o.status));
    if (!active) return;
    const t = setInterval(() => void load(), 15000);
    return () => clearInterval(t);
  }, [signedIn, orders, load]);

  const cancel = async (id: string) => {
    try { await callFn('cancel_my_order', { orderId: id }); toast('Order cancelled.', 'ok'); void load(); }
    catch (e) { toast(e instanceof Error ? e.message : 'Could not cancel.', 'err'); }
  };

  if (!signedIn) {
    return (
      <div className="page-narrow center">
        <h1>Your orders</h1>
        {!ready
          ? <p className="muted">Loading…</p>
          : <>
              <p className="muted">No orders on this device yet. Sign in to see orders from your account.</p>
              <Link className="btn" to={`/${slug}/login`} state={{ returnTo: `/${slug}/orders` }}>Sign in</Link>
            </>}
      </div>
    );
  }
  if (!boundHere) {
    return <div className="page-narrow center"><h1>Your orders</h1><p className="muted">Your account is not set up for this store.</p></div>;
  }
  return (
    <div className="page-narrow">
      <h1>Your orders</h1>
      {offer && isGuest && navState?.contact && (
        <SaveDetailsCard slug={slug} contact={navState.contact} onDone={() => setOffer(false)} />
      )}
      {!orders && <p className="muted">Loading…</p>}
      {orders && orders.length === 0 && <p className="muted">No orders yet.</p>}
      <div className="order-list">
        {orders?.map((o) => (
          <OrderCard key={o.id} order={o} highlight={o.id === highlight}
            actions={o.status === 'pending' ? [{ label: 'Cancel order', kind: 'ghost', onClick: () => void cancel(o.id) }] : []} />
        ))}
      </div>
    </div>
  );
}
